import React, { useContext, useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../contexts/authcontext';
import { Link as ScrollLink } from 'react-scroll';
import { toast } from 'react-toastify';
import NotificationIcon from '../components/Notifications';
import '../style.css';

const CustomNavbar = () => {
  // incomingCall was destructured here but the auth context never provided it,
  // so the call badge was permanently invisible and handleCallIconClick could
  // never fire. GlobalIncomingCallHandler already shows a full modal for
  // incoming calls, so the navbar icon was redundant as well as broken.
  const { isAuthenticated, user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const isClient = user?.role === 'client';

  const isDoctorSide = location.pathname.startsWith('/doctor/') ||
                      location.pathname === '/dochome' ||
                      location.pathname === '/doctordashboard' ||
                      location.pathname.startsWith('/doctordashboard/');

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsDropdownOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
      toast.success('Logged out successfully', {
        position: 'top-center',
        draggable: true,
      });
    } catch (error) {
      toast.error('Error logging out. Please try again.', {
        position: 'top-center',
        draggable: true,
      });
    }
  };

  const toggleDropdown = () => setIsDropdownOpen(!isDropdownOpen);
  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);

  const homeRoute = isDoctorSide ? '/dochome' : '/';

  const navLinks = () => {
    if (isAuthenticated) {
      return (
        <>
          <li>
            <Link to="/doctors">Therapists</Link>
          </li>

          {/* Only clients have bookings to manage, and /my-bookings is a
              ClientRoute — showing it to a therapist would just bounce them. */}
          {isClient && (
            <li>
              <Link to="/my-bookings">My Sessions</Link>
            </li>
          )}

          <li>
            <Link to="/services">Services</Link>
          </li>
          <li>
            {/* ✅ FIXED: was /liveChat — a disconnected mock page (fake
                doctors, hardcoded localhost socket). Now points at the real
                messaging system, tied to actual bookings. */}
            <Link to="/messages" id="messagesBtn">
              Messages
            </Link>
          </li>
          <li>
            <NotificationIcon />
          </li>
          <li id="userProfile" onClick={toggleDropdown}>
            Hi, {user?.name || 'Guest'}
            <div className="user-icon"></div>
            {isDropdownOpen && (
              <ul className="dropdown">
                {isClient && (
                  <li>
                    <Link to="/my-bookings" className="dropdown-item">
                      My Sessions
                    </Link>
                  </li>
                )}
                <li>
                  <Link to="/profile" className="dropdown-item">
                    Profile
                  </Link>
                </li>
                <li>
                  <Link to="/report-problem" className="dropdown-item">
                    Report a problem
                  </Link>
                </li>
                <li>
                  <button onClick={handleLogout} className="dropdown-item">
                    Logout
                  </button>
                </li>
              </ul>
            )}
          </li>
        </>
      );
    }

    return (
      <>
        <li>
          <Link to="/doctors">Therapists</Link>
        </li>
        <li>
          <Link to="/services">Services</Link>
        </li>
        <li>
          <ScrollLink to="aboutus" smooth={true} duration={500} style={{ cursor: 'pointer' }}>
            About
          </ScrollLink>
        </li>
        <li>
          <ScrollLink to="contactus" smooth={true} duration={500} style={{ cursor: 'pointer' }}>
            Contact
          </ScrollLink>
        </li>
        <li>
          <Link to="/login" id="loginBtn">
            Login
          </Link>
        </li>
        <li>
          <Link to="/signup" id="signupBtn">
            Sign Up
          </Link>
        </li>
      </>
    );
  };

  const mobileNavLinks = () => {
    if (isAuthenticated) {
      return (
        <>
          <li><Link to="/doctors">Therapists</Link></li>
          {isClient && <li><Link to="/my-bookings">My Sessions</Link></li>}
          <li><Link to="/services">Services</Link></li>
          <li><Link to="/messages">Messages</Link></li>
          <li><Link to="/profile">Profile</Link></li>
          <li><Link to="/report-problem">Report a Problem</Link></li>
          <li><button onClick={handleLogout}>Logout</button></li>
        </>
      );
    }

    return (
      <>
        <li><Link to="/doctors">Therapists</Link></li>
        <li><Link to="/services">Services</Link></li>
        <li><Link to="/login">Login</Link></li>
        <li><Link to="/signup">Sign Up</Link></li>
      </>
    );
  };

  return (
    <>
      <section id="topbar">
        <Link to={homeRoute}>
          <img src="/img/logo.png" id="logo" alt="UXAAH Logo" />
        </Link>

        {/* Desktop Navigation */}
        <ul id="navbar">
          {navLinks()}
        </ul>

        {/* Hamburger Button (mobile only) */}
        <button
          className={`hamburger-btn ${isMobileMenuOpen ? 'open' : ''}`}
          onClick={toggleMobileMenu}
          aria-label="Toggle navigation menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </section>

      {/* Mobile Navigation Overlay */}
      <div
        className={`mobile-nav-overlay ${isMobileMenuOpen ? 'open' : ''}`}
        onClick={() => setIsMobileMenuOpen(false)}
      />

      {/* Mobile Navigation Drawer */}
      <ul className={`mobile-nav-drawer ${isMobileMenuOpen ? 'open' : ''}`}>
        {mobileNavLinks()}
      </ul>
    </>
  );
};

export default CustomNavbar;